/**
 * AskPenneyIcons.jsx
 * SVG icons for Ask Penney UI - extracted from Figma design
 */

import React from 'react';
import PropTypes from 'prop-types';

// Sparkles icon (used in header title, hero, input, and typing indicator)
// SVG path from Figma SSUX-5980 - 3 sparkle stars design
// Default gray (#666666) in input, red (#E71324) when typing or in header
export const SparklesIcon = ({ className, size = 26, color = '#E71324' }) => (
    <svg
        className={className}
        width={size}
        height={size}
        viewBox="0 0 26 26"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M12.7062 6.28854C12.8368 6.28854 12.9021 6.21235 12.9347 6.09266C13.272 4.2754 13.2503 4.23189 15.1437 3.87277C15.2743 3.85104 15.3504 3.77485 15.3504 3.64425C15.3504 3.51369 15.2743 3.4375 15.1437 3.41572C13.2612 3.03492 13.3156 2.99137 12.9347 1.19588C12.9021 1.07619 12.8368 1 12.7062 1C12.5756 1 12.5103 1.07619 12.4777 1.19588C12.0968 2.99137 12.1621 3.03492 10.2687 3.41572C10.149 3.4375 10.062 3.51369 10.062 3.64425C10.062 3.77485 10.149 3.85104 10.2687 3.87277C12.1621 4.25367 12.1403 4.2754 12.4777 6.09266C12.5103 6.21235 12.5756 6.28854 12.7062 6.28854ZM7.43948 13.7751C7.64623 13.7751 7.7877 13.6445 7.80943 13.4487C8.20119 10.5433 8.29911 10.5433 11.3025 9.96656C11.4984 9.93388 11.6398 9.80332 11.6398 9.59657C11.6398 9.40069 11.4984 9.25922 11.3025 9.22659C8.29911 8.81309 8.19028 8.71513 7.80943 5.75531C7.7877 5.55947 7.64623 5.418 7.43948 5.418C7.2436 5.418 7.10213 5.55947 7.08036 5.76622C6.72128 8.68249 6.5689 8.67162 3.58735 9.22659C3.39147 9.27013 3.25 9.40069 3.25 9.59657C3.25 9.81419 3.39147 9.93388 3.63085 9.96656C6.59068 10.4453 6.72128 10.5215 7.08036 13.4269C7.10213 13.6445 7.2436 13.7751 7.43948 13.7751ZM14.8173 25.8212C15.1002 25.8212 15.3069 25.6144 15.3613 25.3206C16.1339 19.3574 16.9718 18.4543 22.8697 17.8013C23.1744 17.7687 23.3811 17.5402 23.3811 17.2573C23.3811 16.9744 23.1744 16.7567 22.8697 16.7132C16.9718 16.0603 16.1339 15.1571 15.3613 9.19395C15.3069 8.90015 15.1002 8.70426 14.8173 8.70426C14.5343 8.70426 14.3276 8.90015 14.2841 9.19395C13.5115 15.1571 12.6627 16.0603 6.77565 16.7132C6.46012 16.7567 6.25337 16.9744 6.25337 17.2573C6.25337 17.5402 6.46012 17.7687 6.77565 17.8013C12.6518 18.574 13.4679 19.3683 14.2841 25.3206C14.3276 25.6144 14.5343 25.8212 14.8173 25.8212Z"
            fill={color}
        />
    </svg>
);

// Close icon (X)
export const CloseIcon = ({ className, size = 24 }) => (
    <svg
        className={className}
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M18 6L6 18M6 6L18 18"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

// Arrow icon (for send button and carousel)
export const ArrowIcon = ({
    className,
    size = 24,
    direction = 'right',
    color = 'currentColor',
}) => {
    const rotation = {
        right: 0,
        left: 180,
        up: -90,
        down: 90,
    };

    return (
        <svg
            className={className}
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ transform: `rotate(${rotation[direction]}deg)` }}
        >
            <path
                d="M5 12H19M19 12L12 5M19 12L12 19"
                stroke={color}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
};

// Chevron icon (for carousel navigation)
export const ChevronIcon = ({ className, size = 24, direction = 'right' }) => {
    const rotation = {
        right: 0,
        left: 180,
        up: -90,
        down: 90,
    };

    return (
        <svg
            className={className}
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ transform: `rotate(${rotation[direction]}deg)` }}
        >
            <path
                d="M9 18L15 12L9 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
};

// Chat bubble icon (for trigger button)
export const ChatIcon = ({ className, size = 28 }) => (
    <svg
        className={className}
        width={size}
        height={size}
        viewBox="0 0 28 28"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M14 2C7.373 2 2 6.925 2 13C2 15.55 2.95 17.9 4.55 19.75L2.5 25.5L8.75 23.75C10.35 24.55 12.125 25 14 25C20.627 25 26 20.075 26 14C26 7.925 20.627 3 14 3V2Z"
            fill="currentColor"
        />
        <circle cx="9" cy="14" r="1.5" fill="#E71324" />
        <circle cx="14" cy="14" r="1.5" fill="#E71324" />
        <circle cx="19" cy="14" r="1.5" fill="#E71324" />
    </svg>
);

// Sparkles icon (alternate - for typing indicator)
// Uses the same sparkle design as SparklesIcon but with animation-ready sizing
export const SparklesSmallIcon = ({ className, size = 26, color = '#E71324' }) => (
    <svg
        className={className}
        width={size}
        height={size}
        viewBox="0 0 26 26"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M12.7062 6.28854C12.8368 6.28854 12.9021 6.21235 12.9347 6.09266C13.272 4.2754 13.2503 4.23189 15.1437 3.87277C15.2743 3.85104 15.3504 3.77485 15.3504 3.64425C15.3504 3.51369 15.2743 3.4375 15.1437 3.41572C13.2612 3.03492 13.3156 2.99137 12.9347 1.19588C12.9021 1.07619 12.8368 1 12.7062 1C12.5756 1 12.5103 1.07619 12.4777 1.19588C12.0968 2.99137 12.1621 3.03492 10.2687 3.41572C10.149 3.4375 10.062 3.51369 10.062 3.64425C10.062 3.77485 10.149 3.85104 10.2687 3.87277C12.1621 4.25367 12.1403 4.2754 12.4777 6.09266C12.5103 6.21235 12.5756 6.28854 12.7062 6.28854ZM7.43948 13.7751C7.64623 13.7751 7.7877 13.6445 7.80943 13.4487C8.20119 10.5433 8.29911 10.5433 11.3025 9.96656C11.4984 9.93388 11.6398 9.80332 11.6398 9.59657C11.6398 9.40069 11.4984 9.25922 11.3025 9.22659C8.29911 8.81309 8.19028 8.71513 7.80943 5.75531C7.7877 5.55947 7.64623 5.418 7.43948 5.418C7.2436 5.418 7.10213 5.55947 7.08036 5.76622C6.72128 8.68249 6.5689 8.67162 3.58735 9.22659C3.39147 9.27013 3.25 9.40069 3.25 9.59657C3.25 9.81419 3.39147 9.93388 3.63085 9.96656C6.59068 10.4453 6.72128 10.5215 7.08036 13.4269C7.10213 13.6445 7.2436 13.7751 7.43948 13.7751ZM14.8173 25.8212C15.1002 25.8212 15.3069 25.6144 15.3613 25.3206C16.1339 19.3574 16.9718 18.4543 22.8697 17.8013C23.1744 17.7687 23.3811 17.5402 23.3811 17.2573C23.3811 16.9744 23.1744 16.7567 22.8697 16.7132C16.9718 16.0603 16.1339 15.1571 15.3613 9.19395C15.3069 8.90015 15.1002 8.70426 14.8173 8.70426C14.5343 8.70426 14.3276 8.90015 14.2841 9.19395C13.5115 15.1571 12.6627 16.0603 6.77565 16.7132C6.46012 16.7567 6.25337 16.9744 6.25337 17.2573C6.25337 17.5402 6.46012 17.7687 6.77565 17.8013C12.6518 18.574 13.4679 19.3683 14.2841 25.3206C14.3276 25.6144 14.5343 25.8212 14.8173 25.8212Z"
            fill={color}
        />
    </svg>
);

SparklesIcon.propTypes = {
    className: PropTypes.string,
    size: PropTypes.number,
    color: PropTypes.string,
};
SparklesIcon.defaultProps = { className: undefined, size: 26, color: '#E71324' };

CloseIcon.propTypes = {
    className: PropTypes.string,
    size: PropTypes.number,
};
CloseIcon.defaultProps = { className: undefined, size: 24 };

ArrowIcon.propTypes = {
    className: PropTypes.string,
    size: PropTypes.number,
    direction: PropTypes.oneOf(['right', 'left', 'up', 'down']),
    color: PropTypes.string,
};
ArrowIcon.defaultProps = {
    className: undefined,
    size: 24,
    direction: 'right',
    color: 'currentColor',
};

ChevronIcon.propTypes = {
    className: PropTypes.string,
    size: PropTypes.number,
    direction: PropTypes.oneOf(['right', 'left', 'up', 'down']),
};
ChevronIcon.defaultProps = { className: undefined, size: 24, direction: 'right' };

ChatIcon.propTypes = {
    className: PropTypes.string,
    size: PropTypes.number,
};
ChatIcon.defaultProps = { className: undefined, size: 28 };

SparklesSmallIcon.propTypes = {
    className: PropTypes.string,
    size: PropTypes.number,
    color: PropTypes.string,
};
SparklesSmallIcon.defaultProps = { className: undefined, size: 26, color: '#E71324' };

// Alert/Warning icon - Octagon shape per Figma SSUX-5980
// Used for character limit warning message
export const AlertIcon = ({ className, size = 26, color = '#A32026' }) => (
    <svg
        className={className}
        width={size}
        height={size}
        viewBox="0 0 26 26"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M22.0646 7.77239L17.3219 3.09517C16.9585 2.73009 16.4894 2.51776 15.9757 2.51776L9.17686 2.52951C8.69261 2.52951 8.21926 2.7259 7.87684 3.06916L3.23991 7.64063C2.77328 8.10894 2.51562 8.73335 2.51562 9.39637V15.9032C2.51562 16.4428 2.7246 16.9506 3.10479 17.3316L7.79376 22.0374C8.19408 22.4394 8.72618 22.6601 9.29268 22.6601H15.87C16.4432 22.6601 16.9812 22.436 17.3857 22.0298L21.9127 17.4877C22.3936 17.0051 22.658 16.3648 22.658 15.6833V9.20921C22.658 8.66621 22.4473 8.15594 22.0646 7.77239ZM11.1483 14.0274H14.0253V6.83411H11.1483V14.0274ZM12.5868 18.3446C11.792 18.3446 11.1483 17.7 11.1483 16.9053C11.1483 16.1113 11.792 15.4668 12.5868 15.4668C13.3816 15.4668 14.0253 16.1113 14.0253 16.9053C14.0253 17.7 13.3816 18.3446 12.5868 18.3446Z"
            fill={color}
        />
    </svg>
);

AlertIcon.propTypes = {
    className: PropTypes.string,
    size: PropTypes.number,
    color: PropTypes.string,
};
AlertIcon.defaultProps = { className: undefined, size: 26, color: '#A32026' };
